import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Star, ExternalLink, ThumbsUp, ThumbsDown, ArrowLeft, Target, Settings, CheckCircle2 } from 'lucide-react';
import { LanguageSelector } from '../components/LanguageSelector';
import { AdminBar } from '../components/AdminBar';
import { useTitle } from '../hooks/useTitle';
import { supabase } from '../lib/supabase';
import { AffiliateButton } from '../components/AffiliateButton';

interface AiTool {
  id: string;
  name: string;
  description: string;
  category: string;
  pricing_type: string;
  pricing_details: string;
  features: string[];
  use_cases: string[];
  pros: string[];
  cons: string[];
  website_url: string;
  image_url: string;
  rating: number;
  reviews_count: number;
}

export default function AiToolDetail() {
  const { id } = useParams();
  const { setTitle } = useTitle();
  const [tool, setTool] = useState<AiTool | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTool();
  }, [id]);

  useEffect(() => {
    if (tool) {
      setTitle(`${tool.name} - Outils IA`);
    }
  }, [tool, setTitle]);

  const fetchTool = async () => {
    try {
      const { data, error } = await supabase
        .from('ai_tools')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      setTool(data);
    } catch (error) {
      console.error('Error fetching tool:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating: number) => {
    return [...Array(5)].map((_, index) => (
      <Star
        key={index}
        className={`w-5 h-5 ${
          index < Math.floor(rating)
            ? 'text-yellow-400 fill-yellow-400'
            : 'text-gray-300'
        }`}
      />
    ));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-gray-600">Chargement de l'outil...</div>
      </div>
    );
  }

  if (!tool) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-gray-600">Outil non trouvé</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <LanguageSelector />
      <AdminBar />

      {/* Hero Section */}
      <div className="relative h-[60vh] overflow-hidden">
        <img
          src={tool.image_url}
          alt={tool.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        <div className="absolute inset-0 flex items-end">
          <div className="container mx-auto px-4 pb-16">
            <Link 
              to="/tools" 
              className="inline-flex items-center text-white hover:text-orange-300 mb-8 group transition-colors"
            >
              <ArrowLeft className="w-5 h-5 mr-2 transform group-hover:-translate-x-1 transition-transform" />
              Retour aux outils
            </Link>
            <div className="flex items-center gap-4 mb-4">
              <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-sm">
                {tool.category}
              </span>
              <span className="text-white/90 text-sm">
                {tool.pricing_type}
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 animate-slideUp">
              {tool.name}
            </h1>
            <div className="flex items-center gap-4 text-white/90">
              <div className="flex items-center gap-2">
                {renderStars(tool.rating)}
                <span>({tool.reviews_count} avis)</span>
              </div>
              <a
                href={tool.website_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 hover:text-orange-300 transition-colors"
              >
                Visiter le site
                <ExternalLink className="w-5 h-5" />
              </a>
            </div>
          </div>
          <div className="mt-4">
            <AffiliateButton toolId={tool.id} websiteUrl={tool.website_url} />
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Description */}
            <div className="prose prose-lg max-w-none mb-16">
              <p className="text-xl text-gray-600 leading-relaxed">
                {tool.description}
              </p>
            </div>

            {/* Pricing */}
            <div className="bg-orange-50 rounded-xl p-8 mb-16">
              <h2 className="text-2xl font-bold mb-4">Tarification</h2>
              <div className="text-lg text-gray-700">
                <p className="font-medium">{tool.pricing_type}</p>
                <p className="mt-2">{tool.pricing_details}</p>
              </div>
            </div>

            {/* Features Grid */}
            <div className="mb-16">
              <h2 className="text-2xl font-bold mb-8">Fonctionnalités principales</h2>
              <div className="grid md:grid-cols-2 gap-6">
                {tool.features.map((feature, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-4 p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all"
                  >
                    <Settings className="w-6 h-6 text-orange-500 flex-shrink-0" />
                    <span className="text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Use Cases */}
            <div className="mb-16">
              <h2 className="text-2xl font-bold mb-8">Cas d'utilisation</h2>
              <div className="grid md:grid-cols-2 gap-6">
                {tool.use_cases.map((useCase, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-4 p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all"
                  >
                    <Target className="w-6 h-6 text-orange-500 flex-shrink-0" />
                    <span className="text-gray-700">{useCase}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Pros & Cons */}
            <div className="grid md:grid-cols-2 gap-8">
              {/* Pros */}
              <div>
                <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
                  <ThumbsUp className="w-6 h-6 text-green-500" />
                  Points forts
                </h2>
                <div className="space-y-4">
                  {tool.pros.map((pro, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-3 p-4 bg-green-50 rounded-lg"
                    >
                      <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{pro}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Cons */}
              <div>
                <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
                  <ThumbsDown className="w-6 h-6 text-red-500" />
                  Points d'attention
                </h2>
                <div className="space-y-4">
                  {tool.cons.map((con, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-3 p-4 bg-red-50 rounded-lg"
                    >
                      <CheckCircle2 className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{con}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}